// Libraries
import { Box, Text } from '@chakra-ui/react';
import { ActionMeta, GroupBase, MultiValue, OnChangeValue, Select, SingleValue } from 'chakra-react-select';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
// Components
import { selectStyles } from './Dropdown.styles';
import { DropdownBlanket, DropdownButton, dropdownComponents, multiComponents } from './components';
// Utils
import text from '@translations/en.json';
// Types
import { DropdownProps, MultiSelectionLabels, Option } from './Dropdown.types';

/**
 * The `Dropdown` component is a customizable dropdown selector. This component supports both single and multi-select options.
 *
 * @component
 * @template OptionType - The type of each option in the dropdown.
 * @template IsMulti - A boolean that determines if multiple selections are allowed.
 *
 * @param {DropdownProps<OptionType, IsMulti>} props - The props to configure the Dropdown component.
 * @param {string} props.name - The name of the dropdown.
 * @param {OptionType | null} [props.value=null] - The current selected value(s) in the dropdown.
 * @param {OptionType[]} props.options - The options available in the dropdown.
 * @param {string | MultiSelectionLabels} props.selectionLabel - The label(s) for the selected value(s).
 * @param {boolean} props.isMulti - Whether multiple selections are allowed.
 * @param {string} [props.noOptionsMessage=text.no_options_for] - The message displayed when there are no options to select.
 * @param {(value: OptionType | OptionType[], action: ActionMeta<OptionType>) => void} props.onChange - A callback for when the selected value(s) change.
 * @param {boolean} props.isDisabled - Optional flag indicating whether the component is disabled.
 * @param {boolean} props.centerButtonText - Optional Flag used to to center la label on the dropdown button.
 * @param {BoxProps} props.boxProps - The props to customize the wrapper box around the dropdown.
 *
 * @returns ReactElement} The Dropdown component.
 */
export const Dropdown = <OptionType extends Option, IsMulti extends boolean = false>({
  name,
  value = null,
  options,
  selectionLabel,
  isMulti,
  noOptionsMessage = text.no_options_for,
  onChange,
  isDisabled = false,
  centerButtonText = false,
  ...boxProps
}: DropdownProps<OptionType, IsMulti>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState<(IsMulti extends true ? MultiValue<OptionType> : SingleValue<OptionType>) | null>(
    value
  );
  const buttonRef = useRef<HTMLButtonElement>(null);
  const w = centerButtonText ? 'full' : '';

  /**
   * Helper function to handle the change event.
   *
   * @function
   * @param newValue The new value that was chosen, it can be one or multiple values depending on the isMulti flag.
   * @param actionMeta The specific action that was performed.
   */
  const onChangeHandler = useCallback(
    (newValue: OnChangeValue<OptionType, IsMulti>, actionMeta: ActionMeta<OptionType>) => {
      setState(newValue);
      !isMulti && setIsOpen(false);
      onChange?.(newValue as IsMulti extends true ? OptionType[] : OptionType, actionMeta);
    },
    [isMulti, onChange]
  );

  /**
   * Helper function that generates the string that will be displayed in the dropdown button when choosing a single option.
   *
   * @function
   * @returns string.
   */
  const singleLabel = useCallback(() => {
    if ((state as SingleValue<OptionType>) === null) {
      return selectionLabel as string;
    }
    return (state as SingleValue<OptionType>)?.label ?? '';
  }, [selectionLabel, state]);

  /**
   * Helper function that generates the string that will be displayed in the dropdown button when choosing multiple options.
   *
   * @function
   * @returns string.
   */
  const multiLabel = useCallback(() => {
    if (
      state === null ||
      (state as MultiValue<OptionType>).length === 0 ||
      (state as MultiValue<OptionType>).length === options.length
    ) {
      return `All ${(selectionLabel as MultiSelectionLabels).plural}`;
    } else if ((state as MultiValue<OptionType>).length === 1) {
      return `1 ${(selectionLabel as MultiSelectionLabels).singular}`;
    }
    return `${(state as MultiValue<OptionType>).length} ${(selectionLabel as MultiSelectionLabels).plural}`;
  }, [options.length, selectionLabel, state]);

  const message = isMulti ? multiLabel() : singleLabel();

  /**
   * Helper function that generates the no options message to be displayed.
   *
   * @function
   * @returns string.
   */
  const createNoOptionsMessage = (obj: { inputValue: string }) => `${noOptionsMessage} ${obj.inputValue}`;

  /**
   * Function to handle a click event on the blanket (overlay) of a dropdown or modal.
   *
   * @function
   */
  const handleBlanketClick = () => {
    setIsOpen(false);
  };

  isMulti ? multiLabel() : singleLabel();

  /**
   * Effect used to focus the select element when the dropdown menu is opened.
   *
   * @effect
   */
  useEffect(() => {
    if (isOpen) {
      buttonRef.current?.focus();
    }
  }, [isOpen]);

  /**
   * Effect used to keep the external state from value in sync with internal state.
   *
   * @effect
   */
  useEffect(() => {
    setState(value);
  }, [value]);

  return (
    <Box position='relative' minW={32} w={w} {...boxProps}>
      <DropdownButton innerRef={buttonRef} isOpen={isOpen} setIsOpen={setIsOpen} isDisabled={isDisabled}>
        <Text fontSize='m' color='text.base' w={w}>
          {message}
        </Text>
      </DropdownButton>
      {isOpen && (
        <Fragment>
          <Box
            position='absolute'
            mt={2}
            w='full'
            bg='white'
            zIndex='dropdown'
            border='greyCheck'
            borderRadius='md'
            boxShadow='dropdown'
            borderBottomLeftRadius='none'
            borderBottomRightRadius='none'
          >
            <Select<OptionType, IsMulti, GroupBase<OptionType>>
              size='sm'
              menuIsOpen
              name={name}
              value={state}
              options={options}
              isMulti={isMulti}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              backspaceRemovesValue={false}
              components={{
                ...dropdownComponents(setIsOpen),
                ...(isMulti && multiComponents()),
              }}
              chakraStyles={{
                ...selectStyles(),
              }}
              noOptionsMessage={createNoOptionsMessage}
              onChange={onChangeHandler}
            />
          </Box>
          <DropdownBlanket onClick={handleBlanketClick} zIndex='docked' />
        </Fragment>
      )}
    </Box>
  );
};
